import React, { useState, useMemo } from "react";
import styles from "./VehiclesPage.module.css";
import { Vehicle } from "./types";
import { mockVehicles } from "./mockData";

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState<Vehicle[]>(mockVehicles);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDrawer, setShowDrawer] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const filteredVehicles = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return vehicles;

    return vehicles.filter((vehicle) =>
      vehicle.plate.toLowerCase().includes(q) ||
      vehicle.model.toLowerCase().includes(q) ||
      vehicle.bodyType.toLowerCase().includes(q)
    );
  }, [vehicles, searchQuery]);

  const handleCreate = () => {
    setIsCreating(true);
    setEditingVehicle({
      id: "",
      plate: "",
      model: "",
      bodyType: "Sedan",
      capacity: 4,
      packageCapacity: 4,
      status: "Active",
    });
    setShowDrawer(true);
  };

  const handleEdit = (vehicle: Vehicle) => {
    setIsCreating(false);
    setEditingVehicle({ ...vehicle });
    setShowDrawer(true);
  };

  const handleCloseDrawer = () => {
    setShowDrawer(false);
    setEditingVehicle(null);
    setIsCreating(false);
  };

  const handleSave = () => {
    if (!editingVehicle) return;

    if (isCreating) {
      const newVehicle = {
        ...editingVehicle,
        id: Date.now().toString(),
      };
      setVehicles((prev) => [...prev, newVehicle]);
    } else {
      setVehicles((prev) =>
        prev.map((v) => (v.id === editingVehicle.id ? editingVehicle : v))
      );
    }

    handleCloseDrawer();
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this vehicle?")) {
      setVehicles((prev) => prev.filter((v) => v.id !== id));
    }
  };

  const updateField = (field: keyof Vehicle, value: any) => {
    if (editingVehicle) {
      setEditingVehicle({ ...editingVehicle, [field]: value });
    }
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Vehicles</h1>
        </div>
        <div className={styles.headerRight}>
          <button className={styles.columnsBtn}>Columns</button>
          <button className={styles.resetBtn}>Reset</button>
          <button className={styles.createBtn} onClick={handleCreate}>
            + Create
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>IMAGE</th>
              <th>PLATE</th>
              <th>MODEL</th>
              <th>BODY</th>
              <th>CAPACITY</th>
              <th>PACKAGE</th>
              <th>STATUS</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredVehicles.map((vehicle) => (
              <tr key={vehicle.id}>
                <td>
                  <img
                    src="/cars/sports-car.svg"
                    alt={`${vehicle.model} icon`}
                    className={styles.vehicleImage}
                  />
                </td>
                <td className={styles.plate}>{vehicle.plate}</td>
                <td>{vehicle.model}</td>
                <td>{vehicle.bodyType}</td>
                <td>{vehicle.capacity}</td>
                <td>{vehicle.packageCapacity}</td>
                <td>
                  <span
                    className={`${styles.statusBadge} ${
                      styles[vehicle.status.toLowerCase()]
                    }`}
                  >
                    {vehicle.status}
                  </span>
                </td>
                <td>
                  <div className={styles.actions}>
                    <button
                      className={styles.actionBtn}
                      title="More"
                      onClick={() => {}}
                    >
                      ⋯
                    </button>
                    <button
                      className={styles.actionBtn}
                      title="Edit"
                      onClick={() => handleEdit(vehicle)}
                    >
                      ✎
                    </button>
                    <button
                      className={styles.actionBtn}
                      title="View"
                      onClick={() => handleEdit(vehicle)}
                    >
                      ⤢
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Results count */}
      <div className={styles.footer}>
        <span className={styles.resultsCount}>
          Showing {filteredVehicles.length} of {vehicles.length} vehicles
        </span>
      </div>

      {/* Drawer and form stay the same as you had, just without any image field */}
    </div>
  );
}
